/**
 * Phase 10 R3 — useTicketTechPayLines
 *
 * Pay-side ONLY. Reads the 6 tech_pay_* columns for a ticket and returns
 * per-line pay snapshots + ticket totals. Never feeds the scheduler /
 * sell / cost paths.
 *
 * Composite PK on the underlying base table: (ticket_id, job_id).
 *
 * AUTHORITY (FF-A / FF-F):
 *   Reads from `public.v_ticket_jobs_tech_pay` (SECURITY INVOKER view,
 *   migration `20260503120000_ff_f_labor_read_authority.sql`) — NOT
 *   directly from `ticket_jobs`. The view masks the 6 tech-pay columns
 *   to NULL when the parent shop is not entitled
 *   (`tier IN (CATALYST,ENTERPRISE) OR addons @> labor_engine`).
 *   Existing ticket_jobs RLS still applies via SECURITY INVOKER.
 *   For non-entitled shops every line surfaces with
 *   `tech_pay_status = null` and the reducer routes it through
 *   `uncomputed_count` — totals.total_tech_pay_amount stays 0.
 *   FF-A continues to gate WRITES.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { TechPayMode, TechPayStatus } from '@/lib/labor/computeJobTechPay';

export interface TicketTechPayLine {
  ticket_id: string;
  job_id: string;
  job_name: string;
  tech_pay_mode: TechPayMode | null;
  tech_pay_minutes: number | null;
  tech_pay_rate_applied: number | null;
  tech_pay_amount: number | null;
  tech_pay_reason: string | null;
  tech_pay_status: TechPayStatus | null;
  /** True when no snapshot has been written yet (status IS NULL). */
  is_uncomputed: boolean;
}

export interface TicketTechPayTotals {
  total_tech_pay_amount: number;
  computed_count: number;
  deferred_hourly_count: number;
  no_config_count: number;
  uncomputed_count: number;
}

export interface UseTicketTechPayLinesResult {
  lines: TicketTechPayLine[];
  totals: TicketTechPayTotals;
}

export function useTicketTechPayLines(
  ticketId: string | undefined,
  enabled = true,
) {
  return useQuery<UseTicketTechPayLinesResult>({
    queryKey: ['ticket-tech-pay-lines', ticketId],
    enabled: !!ticketId && enabled,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('v_ticket_jobs_tech_pay')
        .select(
          `ticket_id, job_id,
           tech_pay_mode, tech_pay_minutes, tech_pay_rate_applied,
           tech_pay_amount, tech_pay_reason, tech_pay_status,
           job_name`,
        )
        .eq('ticket_id', ticketId)
        .is('deleted_at', null);
      if (error) throw error;

      const lines: TicketTechPayLine[] = (data ?? []).map((r: any) => ({
        ticket_id: r.ticket_id,
        job_id: r.job_id,
        job_name: r.job_name ?? 'Unnamed job',
        tech_pay_mode: r.tech_pay_mode ?? null,
        tech_pay_minutes:
          r.tech_pay_minutes != null ? Number(r.tech_pay_minutes) : null,
        tech_pay_rate_applied:
          r.tech_pay_rate_applied != null ? Number(r.tech_pay_rate_applied) : null,
        tech_pay_amount:
          r.tech_pay_amount != null ? Number(r.tech_pay_amount) : null,
        tech_pay_reason: r.tech_pay_reason ?? null,
        tech_pay_status: (r.tech_pay_status as TechPayStatus | null) ?? null,
        is_uncomputed: r.tech_pay_status == null,
      }));

      const totals = lines.reduce<TicketTechPayTotals>(
        (acc, l) => {
          if (l.tech_pay_status === 'computed' && l.tech_pay_amount != null) {
            acc.total_tech_pay_amount += l.tech_pay_amount;
            acc.computed_count += 1;
          } else if (l.tech_pay_status === 'deferred_hourly') {
            acc.deferred_hourly_count += 1;
          } else if (l.tech_pay_status === 'no_config') {
            acc.no_config_count += 1;
          } else {
            acc.uncomputed_count += 1;
          }
          return acc;
        },
        {
          total_tech_pay_amount: 0,
          computed_count: 0,
          deferred_hourly_count: 0,
          no_config_count: 0,
          uncomputed_count: 0,
        },
      );
      totals.total_tech_pay_amount =
        Math.round(totals.total_tech_pay_amount * 100) / 100;

      return { lines, totals };
    },
  });
}
